import { useReducer, useState, type FormEvent } from 'react';
import cn from 'classnames';
import { Button } from '@warp-ds/react/components/button';
import { TextField } from '@warp-ds/react/components/textfield';
import { IconUser16 } from '@warp-ds/icons/react/user-16';

import { t } from 'shared/locale/texts';
import { useConfig } from 'shared/config/ConfigContext';
import { getOneYearFromToday } from 'shared/util/calendar';
import { storeLastSearchHotel } from 'shared/search/common/lastSearch';
import { trackSearchClick } from 'shared/search/common/tracking';

import { FrontpageDatePickerWrapper } from '../../../components/warp/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { Dropdown } from '../common/Dropdown';
import { FrontpageModal } from '../common/FrontpageModal';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { hotelAutocompleteMapper } from '../common/autocomplete/AutocompleteMapper';

import { useHotelInitialState } from './hooks/useHotelInitialState';
import { useReferralTracking } from './hooks/useReferralTracking';
import { useHotelValidation } from './hooks/useHotelValidation';

import { formatRoomInfoText } from './utils/formatRoomInfoText';
import { buildHotelTrackingItem } from './utils/buildHotelTrackingItem';

import actions, { reducer } from '../../hotel/hotelActions';
import { hotelDefaultState } from '../../hotel/hotelDefaultState';
import { createSubmitUrl } from '../../hotel/kayakWhitelabel';
import { HotelRooms } from './HotelRooms';
import { HotelHiddenInputs } from './utils/HotelHiddenInputs';

import styles from './HotelSearch.module.css';

type HotelSearchProps = {
    prefill?: Partial<typeof hotelDefaultState>;
    position: string;
    unleash?: Record<string, boolean>;
};

export const HotelSearch = ({ prefill, position, unleash }: HotelSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, { ...hotelDefaultState, ...prefill });

    const { hotelAutocompleteUrl, hotelResultUrl } = useConfig();

    useHotelInitialState(dispatch, prefill);
    useReferralTracking();

    const { rooms = [], startDate, endDate, destination, destinationContext } = state;
    const { isValid, invalid } = useHotelValidation(state, pristine, unleash);

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        if (!isValid) {
            setPristine(false);
            event.preventDefault();
        } else {
            const item = buildHotelTrackingItem({ destinationContext, rooms, startDate, endDate });
            trackSearchClick(
                'Search hotel',
                '/reise/hotell',
                'hotel',
                unleash?.kayakWhitelabel ? `${position} - whitelabel` : position,
                item,
            );
            storeLastSearchHotel(state);
        }
    };

    return (
        <SearchPanelWrapper
            className={styles['form-body']}
            onSubmit={handleSubmit}
            action={unleash?.kayakWhitelabel ? 'https://travel-kayak-wl.finn.no/in' : createSubmitUrl(hotelResultUrl, destinationContext)}
            method="GET"
            dataTestId="hotelSearch">
            <Autocomplete
                vertical="hotel"
                id="destination"
                label={t('hotel.destination.label')}
                placeholder={t('hotel.destination.placeholder')}
                defaultValue={destination}
                onSelect={(option) => dispatch(actions.setDestination(option))}
                url={hotelAutocompleteUrl}
                mapOptions={hotelAutocompleteMapper}
                error={invalid.destination ? t('hotel.validation.destination') : undefined}
            />
            <FrontpageDatePickerWrapper
                upperDateLimit={getOneYearFromToday()}
                startDate={startDate}
                endDate={endDate}
                useRange
                useAnchor={{
                    invalid: invalid.startDate || invalid.endDate || invalid.dateRange,
                    helpText: invalid.startDate || invalid.endDate || invalid.dateRange ? 'Du må velge dato' : undefined,
                }}
                onDateChange={({ startDate, endDate }) => {
                    dispatch(actions.setDates({ startDate, endDate }));
                }}>
                <TextField
                    id="date-range-start"
                    data-testid="Hotel-dateRangeStart"
                    label={t('hotel.checkin.label')}
                    invalid={invalid.startDate || invalid.dateRange}
                    helpText={invalid.startDate && t('hotel.validation.dateRangeStart')}
                />
                <TextField
                    id="date-range-end"
                    data-testid="Hotel-dateRangeEnd"
                    label={t('hotel.checkout.label')}
                    invalid={invalid.endDate || invalid.dateRange}
                    helpText={
                        invalid.endDate
                            ? t('hotel.validation.dateRangeEnd')
                            : invalid.dateRange
                              ? t('hotel.validation.dateRangeGap')
                              : undefined
                    }
                />
            </FrontpageDatePickerWrapper>
            <FrontpageModal
                breakpoint="sm"
                title="Gjester og rom"
                trigger={{
                    text: formatRoomInfoText(rooms),
                    prefix: <IconUser16 aria-hidden />,
                }}
                error={invalid.rooms ? t('hotel.validation.rooms') : undefined}>
                <HotelRooms rooms={rooms} dispatch={dispatch} />
            </FrontpageModal>
            <Dropdown
                trigger={{ text: formatRoomInfoText(rooms), label: 'Reisende' }}
                prefix={<IconUser16 aria-hidden />}
                className="hidden sm:flex">
                <HotelRooms rooms={rooms} dispatch={dispatch} />
            </Dropdown>
            <HotelHiddenInputs
                destinationContext={destinationContext}
                startDate={startDate}
                endDate={endDate}
                rooms={rooms}
                unleash={unleash}
            />
            <Button
                className={cn(styles['search-button'], {
                    [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                })}
                fullWidth
                type="submit"
                primary
                data-testid="hotelSearchButton">
                {t('action.search')}
            </Button>
        </SearchPanelWrapper>
    );
};
